import React, { useState, useEffect } from 'react';
import useWebSocket from '../hooks/useWebSocket';
import gameService from '../services/gameService';
import PlayerStatus from './PlayerStatus';
import PlayerHand from './PlayerHand';
import Card from './Card';
import ActionAnimation from './ActionAnimation';
import MessagePanel from './MessagePanel';
import AIThinkingIndicator from './AIThinkingIndicator';
import useTranslation from '../i18n/useTranslation';
import '../styles/pixel-art.css';

const GameBoard = ({ onGameOver, onReturnToLobby }) => {
  const [gameState, setGameState] = useState(gameService.getState() || null);
  const [phase, setPhase] = useState(gameService.getState()?.phase || 'waiting'); // 'waiting', 'selecting', 'playing', 'gameover'
  const [selectedLifeCards, setSelectedLifeCards] = useState([]);
  const [selectedDefenseCard, setSelectedDefenseCard] = useState(null);
  const [actionMode, setActionMode] = useState(null); // null, 'attack', 'changeDefense'
  const [notification, setNotification] = useState('');
  const [currentAction, setCurrentAction] = useState(null);
  const [animationQueue, setAnimationQueue] = useState([]);
  const [pendingGameOver, setPendingGameOver] = useState(false);
  const [messages, setMessages] = useState([]);
  const [aiThinking, setAiThinking] = useState(null);

  const { isConnected, reconnecting, reconnectAttempt, selectCards, attack, changeDefense, sendPlayerMessage, leaveRoom, onMessage, MSG_TYPES } = useWebSocket();
  const { t } = useTranslation();

  const showNotification = (message) => {
    setNotification(message);
    setTimeout(() => setNotification(''), 3000);
  };

  useEffect(() => {
    // Get initial state
    const initialState = gameService.getState();
    if (initialState) {
      setGameState(initialState);
      if (initialState.phase) {
        setPhase(initialState.phase);
      }
      // If we already have dealt cards, we're in selecting phase
      if (initialState.dealtCards && initialState.dealtCards.length > 0) {
        setPhase('selecting');
        showNotification(t.selection.selectCardsNotification);
      }
    }

    // Subscribe to game state changes
    const unsubscribe = gameService.subscribe((state) => {
      setGameState(state);
      if (state?.phase) {
        setPhase(state.phase);
      }
    });

    return unsubscribe;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Listen for cards dealt
    const unsubscribeDealt = onMessage(MSG_TYPES.CARDS_DEALT, () => {
      setPhase('selecting');
      showNotification(t.selection.selectCardsNotification);
    });

    // Listen for game state updates
    const unsubscribeState = onMessage(MSG_TYPES.GAME_STATE, () => {
      setPhase('playing');
      setActionMode(null);
    });

    // Listen for action results
    const unsubscribeAction = onMessage(MSG_TYPES.ACTION_RESULT, (message) => {
      if (message.data) {
        // Add animation to queue instead of showing immediately
        setAnimationQueue(queue => [...queue, message.data]);
        setActionMode(null);
      }
    });

    // Listen for game over
    const unsubscribeGameOver = onMessage(MSG_TYPES.GAME_OVER, () => {
      // Don't immediately show game over - wait for animations to complete
      setPendingGameOver(true);
    });

    // Listen for return to lobby
    const unsubscribeReturnToLobby = onMessage(MSG_TYPES.RETURN_TO_LOBBY, () => {
      if (onReturnToLobby) {
        onReturnToLobby();
      }
    });

    // Listen for AI thinking
    const unsubscribeAIThinking = onMessage(MSG_TYPES.AI_THINKING, (message) => {
      if (message.data) {
        setAiThinking({
          playerName: message.data.playerName || 'AI'
        });
      }
    });

    // Listen for AI actions
    const unsubscribeAIAction = onMessage(MSG_TYPES.AI_ACTION, () => {
      setAiThinking(null);
    });

    // Listen for AI messages
    const unsubscribeAIMessage = onMessage(MSG_TYPES.AI_MESSAGE, (message) => {
      if (message.data) {
        setMessages(prev => [...prev, {
          from: message.data.from,
          fromName: message.data.fromName,
          to: message.data.to,
          toName: message.data.toName,
          text: message.data.text,
          isAI: true,
          timestamp: message.data.timestamp || Date.now()
        }]);
      }
    });

    // Listen for messages
    const unsubscribeReceiveMessage = onMessage(MSG_TYPES.RECEIVE_MESSAGE, (message) => {
      if (message.data) {
        setMessages(prev => [...prev, {
          from: message.data.from,
          fromName: message.data.fromName,
          to: message.data.to,
          toName: message.data.toName,
          text: message.data.text,
          isAI: message.data.isAI || false,
          timestamp: message.data.timestamp || Date.now()
        }]);
      }
    });

    return () => {
      unsubscribeDealt();
      unsubscribeState();
      unsubscribeAction();
      unsubscribeGameOver();
      unsubscribeReturnToLobby();
      unsubscribeAIThinking();
      unsubscribeAIAction();
      unsubscribeAIMessage();
      unsubscribeReceiveMessage();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onMessage, MSG_TYPES, onReturnToLobby]);

  const handleCardSelection = (card) => {
    if (selectedDefenseCard?.id === card.id) {
      setSelectedDefenseCard(null);
      return;
    }

    const isLifeCard = selectedLifeCards.some(c => c.id === card.id);
    if (isLifeCard) {
      setSelectedLifeCards(selectedLifeCards.filter(c => c.id !== card.id));
    } else if (selectedLifeCards.length < 2) {
      setSelectedLifeCards([...selectedLifeCards, card]);
    }
  };

  const handleDefenseCardSelection = (card) => {
    if (selectedLifeCards.some(c => c.id === card.id)) {
      return;
    }
    setSelectedDefenseCard(card);
  };

  const handleConfirmSelection = () => {
    if (selectedLifeCards.length !== 2 || !selectedDefenseCard) {
      showNotification(t.selection.selectCardsError);
      return;
    }

    const lifeCardIds = selectedLifeCards.map(c => c.id);
    selectCards(lifeCardIds, selectedDefenseCard.id);
    setPhase('waiting');
    showNotification(t.selection.waitingForPlayers);
  };

  const handlePlayerSelect = (playerId) => {
    if (!actionMode) return;

    if (actionMode === 'attack') {
      attack(playerId);
      showNotification(t.playing.attacking);
    } else if (actionMode === 'changeDefense') {
      changeDefense(playerId);
      showNotification(t.playing.changingDefense);
    }
    setActionMode(null);
  };

  const handleSendMessage = (recipientId, text) => {
    if (text.trim()) {
      sendPlayerMessage(recipientId, text);
    }
  };

  const handleLeaveGame = () => {
    const message = phase === 'gameover'
      ? t.common?.confirmLeaveGameOver || 'Are you sure you want to leave?'
      : t.common?.confirmLeaveGame || 'Are you sure you want to leave the game? You will lose your progress.';

    if (window.confirm(message)) {
      leaveRoom();
      if (onReturnToLobby) {
        onReturnToLobby();
      }
    }
  };

  const renderSelectionPhase = () => {
    const dealtCards = gameState?.dealtCards || [];

    return (
      <div className="game-phase selection-phase">
        <div className="phase-header">
          <div className="phase-header-content">
            <h2 className="pixel-text">{t.selection.title}</h2>
            <p>{t.selection.instructions}</p>
          </div>
          <button
            className="pixel-button pixel-button-danger leave-game-button"
            onClick={handleLeaveGame}
            title={t.common?.leaveGame || 'Leave Game'}
          >
            {t.common?.leaveGame || 'Leave'}
          </button>
        </div>

        <div className="selection-container">
          <div className="selection-section">
            <h3>{t.selection.lifeCards} ({selectedLifeCards.length}/2)</h3>
            <div className="selection-cards">
              {dealtCards.map(card => (
                <Card
                  key={card.id}
                  card={card}
                  onClick={handleCardSelection}
                  selected={selectedLifeCards.some(c => c.id === card.id)}
                  disabled={selectedDefenseCard?.id === card.id}
                />
              ))}
            </div>
          </div>

          <div className="selection-section">
            <h3>{t.selection.defenseCard} ({selectedDefenseCard ? '1/1' : '0/1'})</h3>
            <div className="selection-cards">
              {dealtCards.map(card => (
                <Card
                  key={card.id}
                  card={card}
                  onClick={handleDefenseCardSelection}
                  selected={selectedDefenseCard?.id === card.id}
                  disabled={selectedLifeCards.some(c => c.id === card.id)}
                />
              ))}
            </div>
          </div>
        </div>

        <button
          className="pixel-button pixel-button-primary confirm-button"
          onClick={handleConfirmSelection}
          disabled={selectedLifeCards.length !== 2 || !selectedDefenseCard}
        >
          {t.selection.confirmSelection}
        </button>
      </div>
    );
  };

  const renderPlayingPhase = () => {
    const players = gameState?.players || [];
    const myPlayer = players.find(p => p.id === gameService.playerID);
    const otherPlayers = players.filter(p => p.id !== gameService.playerID);
    const isMyTurn = gameService.isMyTurn();
    const isAlive = myPlayer?.isAlive || false;

    return (
      <div className="game-phase playing-phase">
        <div className="game-header">
          <div className="game-info">
            <span className="deck-count">{t.playing.deck}: {gameState?.deckCount || 0}</span>
            {isMyTurn && isAlive && (
              <span className="turn-indicator pixel-text">{t.playing.yourTurn}</span>
            )}
          </div>
          <button
            className="pixel-button pixel-button-danger leave-game-button"
            onClick={handleLeaveGame}
            title={t.common?.leaveGame || 'Leave Game'}
          >
            {t.common?.leaveGame || 'Leave'}
          </button>
        </div>

        <div className="game-board">
          <div className="opponents-section">
            <h3>{t.playing.opponents}</h3>
            <div className="opponents-grid">
              {otherPlayers.map(player => (
                <PlayerStatus
                  key={player.id}
                  player={player}
                  isCurrent={player.isCurrent}
                  isMe={false}
                  onSelect={handlePlayerSelect}
                  selectable={isMyTurn && isAlive && actionMode !== null && player.isAlive}
                  gameState={gameState}
                />
              ))}
            </div>
          </div>

          {myPlayer && (
            <div className="my-player-section">
              <div className="my-status">
                <PlayerStatus
                  player={myPlayer}
                  isCurrent={myPlayer.isCurrent}
                  isMe={true}
                  onSelect={handlePlayerSelect}
                  selectable={isMyTurn && isAlive && actionMode === 'changeDefense'}
                  gameState={gameState}
                />
              </div>

              <div className="my-hand-section">
                <h3>{t.playing.yourHand}</h3>
                <PlayerHand cards={gameState?.yourCards || []} />
              </div>

              {isMyTurn && isAlive && (
                <div className="action-buttons">
                  {actionMode === null ? (
                    <>
                      <button
                        className="pixel-button pixel-button-primary"
                        onClick={() => {
                          setActionMode('attack');
                          showNotification(t.playing.selectPlayerToAttack);
                        }}
                      >
                        {t.playing.attack}
                      </button>
                      <button
                        className="pixel-button pixel-button-secondary"
                        onClick={() => {
                          setActionMode('changeDefense');
                          showNotification(t.playing.selectPlayerToChangeDefense);
                        }}
                      >
                        {t.playing.changeDefense}
                      </button>
                    </>
                  ) : (
                    <button
                      className="pixel-button pixel-button-secondary"
                      onClick={() => {
                        setActionMode(null);
                        showNotification(t.playing.actionCancelled);
                      }}
                    >
                      {t.playing.cancel}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <MessagePanel
          messages={messages}
          onSendMessage={handleSendMessage}
          players={gameState?.players || []}
          currentPlayerId={gameService.playerID}
        />

      </div>
    );
  };

  const renderGameOverPhase = () => {
    const winner = gameState?.finalPlayers?.find(p => p.isAlive);

    return (
      <div className="game-phase gameover-phase">
        <div className="gameover-main">
          <div className="gameover-content">
            <h1 className="pixel-text">{t.gameOver.title}</h1>
            {winner && (
              <div className="winner-announcement">
                <h2>{winner.name} {t.gameOver.wins}</h2>
                {winner.id === gameService.playerID && (
                  <div className="victory-message pixel-text">{t.gameOver.victory}</div>
                )}
              </div>
            )}

            <div className="final-standings">
              <h3>{t.gameOver.finalStandings}</h3>
              <div className="standings-list">
                {gameState?.finalPlayers?.map((player, index) => (
                  <div key={player.id} className="standing-item">
                    <span className="standing-rank">{t.gameOver.rank}{index + 1}</span>
                    <span className="standing-name">{player.name}</span>
                    <span className="standing-life">{t.gameOver.life}: {player.life}</span>
                  </div>
                ))}
              </div>
            </div>

            <button
              className="pixel-button pixel-button-primary"
              onClick={onReturnToLobby}
            >
              {t.gameOver.returnToMenu}
            </button>
          </div>

          <MessagePanel
            messages={messages}
            onSendMessage={handleSendMessage}
            players={gameState?.finalPlayers || []}
            currentPlayerId={gameService.playerID}
          />
        </div>
      </div>
    );
  };

  const handleAnimationComplete = () => {
    setCurrentAction(null);
    // Process next animation in queue after a short delay
    setTimeout(() => {
      setAnimationQueue(queue => {
        if (queue.length > 0) {
          const [nextAnimation, ...rest] = queue;
          setCurrentAction(nextAnimation);
          return rest;
        }
        return queue;
      });
    }, 100);
  };

  // Process animation queue - show first animation if none is currently playing
  useEffect(() => {
    if (!currentAction && animationQueue.length > 0) {
      const [nextAnimation, ...rest] = animationQueue;
      setCurrentAction(nextAnimation);
      setAnimationQueue(rest);
    }
  }, [currentAction, animationQueue]);

  // Show game over screen once all animations are complete
  useEffect(() => {
    if (pendingGameOver && !currentAction && animationQueue.length === 0) {
      setPhase('gameover');
      setPendingGameOver(false);
    }
  }, [pendingGameOver, currentAction, animationQueue]);

  return (
    <div className="screen game-screen">
      {reconnecting && (
        <div className="connection-banner reconnecting-banner">
          {t.connection.reconnecting.replace('{attempt}', reconnectAttempt)}
        </div>
      )}
      {!isConnected && !reconnecting && (
        <div className="connection-banner disconnected-banner">
          {t.connection.disconnected}
        </div>
      )}

      {notification && (
        <div className="notification-banner">
          {notification}
        </div>
      )}

      {aiThinking && (
        <AIThinkingIndicator aiName={aiThinking.playerName} />
      )}

      {currentAction && (
        <ActionAnimation
          actionData={currentAction}
          onComplete={handleAnimationComplete}
        />
      )}

      {phase === 'selecting' && renderSelectionPhase()}
      {phase === 'playing' && renderPlayingPhase()}
      {phase === 'gameover' && renderGameOverPhase()}
      {phase === 'waiting' && (
        <div className="waiting-screen">
          <div className="pixel-text">{t.common.waiting}</div>
        </div>
      )}
    </div>
  );
};

export default GameBoard;
